/**
 * QRコードURL解析ユーティリティ
 * QRコードパラメータ（loc, t）の解析と検証
 */

export interface QRCodeParams {
  location: string;
  timestamp?: string;
  sessionToken?: string;
}

export interface ParsedQRCode {
  isValid: boolean;
  params?: QRCodeParams;
  error?: string;
}

/**
 * QRコードURLからパラメータを解析
 */
export function parseQRCode(url: string): ParsedQRCode {
  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);
    
    // 必須パラメータ: loc (location)
    const location = params.get('loc');
    if (!location) {
      return {
        isValid: false,
        error: 'Missing required parameter: loc'
      };
    }

    // location の形式検証（例: "floor-3-east", "entrance-main"）
    if (!isValidLocationFormat(location)) {
      return {
        isValid: false,
        error: 'Invalid location format'
      };
    }

    // オプションパラメータ
    const timestamp = params.get('t');
    const sessionToken = params.get('token');

    // タイムスタンプ検証（存在する場合）
    if (timestamp && !isValidTimestamp(timestamp)) {
      return {
        isValid: false,
        error: 'Invalid timestamp format'
      };
    }

    return {
      isValid: true,
      params: {
        location,
        timestamp: timestamp || undefined,
        sessionToken: sessionToken || undefined
      }
    };

  } catch (error) {
    return {
      isValid: false,
      error: `URL parsing failed: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}

/**
 * location パラメータの形式を検証
 */
function isValidLocationFormat(location: string): boolean {
  // location は英数字、ハイフン、アンダースコアのみ許可
  const locationRegex = /^[a-zA-Z0-9_-]+$/;
  
  // 長さ制限（1-50文字）
  if (location.length < 1 || location.length > 50) {
    return false;
  }

  return locationRegex.test(location);
}

/**
 * タイムスタンプの形式を検証
 */
function isValidTimestamp(timestamp: string): boolean {
  // Unix timestamp (秒) または ISO 8601 形式をサポート
  
  // Unix timestamp チェック
  const unixTimestamp = parseInt(timestamp, 10);
  if (!isNaN(unixTimestamp)) {
    // 合理的な範囲内のタイムスタンプかチェック
    const now = Date.now() / 1000;
    const oneYearAgo = now - (365 * 24 * 60 * 60);
    const oneYearFromNow = now + (365 * 24 * 60 * 60);
    
    return unixTimestamp >= oneYearAgo && unixTimestamp <= oneYearFromNow;
  }

  // ISO 8601 形式チェック
  const date = new Date(timestamp);
  return !isNaN(date.getTime());
}

/**
 * 現在のURLからQRコードパラメータを取得
 */
export function getCurrentQRParams(): ParsedQRCode {
  const currentUrl = window.location.href;
  return parseQRCode(currentUrl);
}

/**
 * QRコードURLを生成（テスト用）
 */
export function generateQRCodeUrl(
  baseUrl: string,
  location: string,
  options: {
    timestamp?: boolean;
    sessionToken?: string;
  } = {}
): string {
  const url = new URL(baseUrl);
  const params = new URLSearchParams();
  
  params.set('loc', location);
  
  if (options.timestamp) {
    params.set('t', Math.floor(Date.now() / 1000).toString());
  }
  
  if (options.sessionToken) {
    params.set('token', options.sessionToken);
  }
  
  url.search = params.toString();
  return url.toString();
}

/**
 * 不正なQRコードかどうかを判定
 */
export function isSuspiciousQRCode(url: string): boolean {
  try {
    const urlObj = new URL(url);
    
    // 外部ドメインへのリダイレクトチェック
    const allowedDomains = [
      'localhost',
      'hep-five.web.app',
      'hep-five.firebaseapp.com'
    ];
    
    const isAllowedDomain = allowedDomains.some(domain =>
      urlObj.hostname === domain || urlObj.hostname.endsWith(`.${domain}`)
    );
    
    if (!isAllowedDomain) {
      return true;
    }
    
    // 異常に長いパラメータチェック
    if (urlObj.search.length > 1000) {
      return true;
    }
    
    // 疑わしいパラメータパターン
    const suspiciousParams = ['script', 'javascript', 'data:', 'file:'];
    const searchParams = urlObj.search.toLowerCase();
    
    return suspiciousParams.some(param => searchParams.includes(param));
    
  } catch {
    // URL解析に失敗した場合は疑わしいと判定
    return true;
  }
}